import React from "react";
import IOFactory from "../factories/interactabeObjectFactory";
import InteractableObject from "../interactableObjects/interactableObject";
import CompositeInteractableObject from "../interactableObjects/compositeInteractableObject";
import dataLoader from "../helpers/dataLoader";
import { toast } from "react-toastify";
import {
  addSnackbar,
  SnackbarVariantValue,
} from "@labxchange/ui-components/lib/Snackbar";

// Type declaration for SCSS module imports
declare module "@learningblocks/protocol-constants/lib/Styles/_exports.module.scss" {
  export const red2: string;
  export const yellow1: string;
  export const red1: string;
  export const blue1: string;
  export const navy1: string;
  export const charcoal1: string;
  // Add other color variables as needed
}

import SceneItem from "../interactableObjects/sceneObject";
import ControllableObject from "../interactableObjects/controllableObject";
import stepManager from "./stepManager";
import ModalManager from "./modalManager";
import {
  IOData,
  SimManagerState,
  Vector3,
  Condition,
  UserInput,
  ItemInfo,
  Snapshot,
  StepInfo,
} from "../types";
import { Label } from "../types/ObjectDefinitionTypes";
import CustomObject from "../interactableObjects/customObject";
import graphics from "../helpers/graphics";
import Toast from "../components/toast/Toast";
import toastStyles from "../components/toast/Toast.module.scss";
import { isHotkeyMatch } from "@learningblocks/shared-components/lib/keyboardUtils";

/**
+ * Safari 13 fallback for structuredClone.
+ */
function deepClone<T>(obj: T): T {
  return typeof structuredClone === "function"
    ? structuredClone(obj)
    : JSON.parse(JSON.stringify(obj));
}

type SimManagerProps = object;

type Dispatch = (action: { type: string; payload: unknown }) => void;

// Use the exported colors directly from the SCSS module

class SimManager extends React.Component<SimManagerProps, SimManagerState> {
  private static instance: SimManager;
  private reduxStore: {
    dispatch: Dispatch;
  } = {
    // eslint-disable-next-line
    dispatch: () => console.error("Store is not set"),
  };
  // eslint-disable-next-line
  private IOItems: JSX.Element[];
  private IOObjects: IOData[];
  private ControllableObjects: ControllableObject[];
  public InteractableObjects: InteractableObject[];
  public CompositeInteractableObjects: CompositeInteractableObject[];
  private SceneItems: SceneItem[];
  private CustomObjects: CustomObject[];
  private OpenModalItem: InteractableObject | null;
  public initialized: boolean;
  private Modals: Array<{ key: string; Component: React.ComponentType<any> }> =
    [];

  public undoSnapshots: { snapshot: Snapshot; id: string }[] = [];
  public undoRemaining: number;

  private restartSnapshots: { snapshot: Snapshot; id: string }[] = [];
  private initialSnapshot: { snapshot: Snapshot; id: string } | null = null;

  public customItemDropFunction: (
    _item: ControllableObject | CustomObject,
    _itemDroppedOn: ControllableObject[] | InteractableObject[]
  ) => boolean;

  public customItemClickFunction: (
    _item: InteractableObject | ControllableObject
  ) => boolean;
  public ejectButtonHandler: (
    _item: ControllableObject | InteractableObject
  ) => boolean;

  public addModals(
    modals: Array<{ key: string; Component: React.ComponentType<any> }>
  ) {
    this.Modals = [...this.Modals, ...modals];
  }
  public getModals() {
    return this.Modals;
  }
  public moveMenuFunction: (_option: string, _item: string) => boolean;
  public customStepsFunction: (stepText: string) => boolean;
  public altTextFunction: (item: string) => boolean;
  public scale: { userScale: number; sceneScale: number };
  private usingNewDesignSystem = false;

  public addToast: (title: string, message: string, type: string) => void;

  private constructor(props: SimManagerProps) {
    super(props);
    this.IOItems = [];
    this.IOObjects = [];
    this.ControllableObjects = [];
    this.InteractableObjects = [];
    this.CompositeInteractableObjects = [];
    this.SceneItems = [];
    this.CustomObjects = [];
    this.OpenModalItem = null;
    this.initialized = false;
    this.undoRemaining = 4;

    // eslint-disable-next-line
    this.customItemDropFunction = () => false;
    // eslint-disable-next-line
    this.customItemClickFunction = () => false;
    // eslint-disable-next-line
    this.ejectButtonHandler = () => false;
    // eslint-disable-next-line
    this.moveMenuFunction = (option: string, item: string) => {
      const targetObject = this.IOObjects.find((object) => object.id === item);
      if (targetObject?.moveMenu?.[option]) {
        const simObject = this.getObject(item);
        if (!simObject) return false;

        let targetPosition = targetObject.moveMenu[option];
        if (
          targetPosition &&
          targetPosition.x === -1 &&
          targetPosition.y === -1
        ) {
          const targetSimObject = this.getObject(option);
          if (!targetSimObject) return false;

          const targetSimObjectPos = targetSimObject.getItemPosition();
          targetPosition = {
            x: targetSimObjectPos.x + 10,
            y: targetSimObjectPos.y + 10,
            z: targetSimObjectPos.z,
          };
        }

        simObject.setItemPosition(targetPosition, false);
        this.handleItemDrop(item, option);
      }
      return false;
    };
    // eslint-disable-next-line
    this.addToast = (_title: string, message: string, type?: string) => {
      if (this.usingNewDesignSystem) {
        addSnackbar(message, type as SnackbarVariantValue);
      } else {
        toast(<Toast type={type} msg={message} />, {
          className: toastStyles.toastWrapper,
          progressClassName: toastStyles.toastProgressBar,
          hideProgressBar: true,
          position: "top-center",
          autoClose: 5000,
          toastId: `toast-${type}-${Date.now()}`,
        });
      }
    };
    // eslint-disable-next-line
    this.customStepsFunction = () => false;
    // eslint-disable-next-line
    this.altTextFunction = () => false;

    this.scale = {
      userScale: 1,
      sceneScale: 1,
    };

    // eslint-disable-next-line
    setTimeout(() => {
      this.saveInitialSnapshot();
      if (stepManager.getInstance().getCurrentObjective() == null) {
        console.error("No objectives loaded in stepManager");
        return;
      }
      this.saveSnapshot(
        stepManager.getInstance().getCurrentObjective().steps[
          stepManager.getInstance().getCurrentStep()
        ].stepID
      );
    }, 800);

    const OVERLAY_HOTKEY = { ctrl: true, key: "b" };
    let overlay: HTMLDivElement | null = null;

    function toggleOverlay() {
      if (overlay) {
        overlay.remove();
        overlay = null;
      } else {
        overlay = document.createElement("div");
        overlay.id = "blindfold-overlay";
        overlay.style.cssText = `
        position:fixed;
        top:0;
        left:0;
        width:100vw;
        height:100vh;
        background:black;
        z-index:999999;
        pointer-events:none;
      `;
        document.body.appendChild(overlay);
      }
    }

    window.addEventListener("keydown", (e) => {
      if (isHotkeyMatch(e, OVERLAY_HOTKEY)) {
        e.preventDefault();
        toggleOverlay();
      }
    });
  }

  static getInstance() {
    if (!this.instance) this.instance = new SimManager({});
    return this.instance;
  }

  static updateObjectState(
    object: string,
    state: string,
    position?: Vector3,
    reset = false
  ) {
    SimManager.getInstance().updateState(object, state, position, reset);
  }

  static setLabelText(id: string, labelText: string) {
    SimManager.getInstance().setLabel(id, labelText);
  }
  static filterInteractableObjects({
    name,
    id,
  }: {
    name?: string;
    id?: string;
  }) {
    const instance = SimManager.getInstance();
    return [
      ...instance.ControllableObjects,
      ...instance.InteractableObjects,
      ...instance.CompositeInteractableObjects,
    ].filter((i) => (id ? i.getId() === id : i.getName() === name));
  }

  static getObjectState(object: string) {
    return SimManager.getInstance().getState(object);
  }

  static getSimDataObject(id: string) {
    return SimManager.getInstance().getSimDataObject(id);
  }

  static getDisplayLabel(id: string): string {
    return SimManager.getInstance().getDisplayLabel(id);
  }

  static addStore(store: { dispatch: Dispatch; getState: () => unknown }) {
    SimManager.getInstance().reduxStore = store;
    // Connect stepManager to the store's dispatch function
    stepManager
      .getInstance()
      .setDispatch(store.dispatch as (action: unknown) => void);
  }

  static getStore() {
    return SimManager.getInstance().reduxStore;
  }

  static dispatchAction(action: string, payload: unknown) {
    action &&
      SimManager.getInstance().reduxStore.dispatch({
        type: action,
        payload,
      });
  }

  createSnapshot(): Snapshot {
    //Create an array of all items
    const allItems: ItemInfo[] = [
      ...this.InteractableObjects.map((item) => ({
        itemID: item.props.id,
        position: item.props.position,
        state: item.getCurrentState(),
        text: item.getText(),
        userInput: item.getCurrentInput(),
      })),
      ...this.ControllableObjects.map((item) => ({
        itemID: item.props.id,
        position: item.getItemPosition(),
        state: item.getCurrentState(),
        text: "",
        userInput: item.getCurrentInput(),
      })),
    ];

    const objectives = stepManager.getInstance().getAllObjectives();
    const allSteps: StepInfo[] = [];

    objectives.forEach((objective) => {
      objective.steps.forEach((step) => {
        const stepInfo: StepInfo = {
          stepID: step.stepID,
          complete: step.completed ? true : false,
          wrong: step.wrong ? true : false,
        };
        allSteps.push(stepInfo);
      });
    });

    //Create a snapshot for storing
    const snapshot: Snapshot = {
      items: allItems,
      currentStep: stepManager.getInstance().getCurrentStep(),
      // eslint-disable-next-line no-undef
      currentObjective: deepClone(
        stepManager.getInstance().getCurrentObjective()
      ),
      stepsList: allSteps,
      currentObjectiveIndex: stepManager.getInstance().getObjectiveIndex(),
    };

    return snapshot;
  }

  saveInitialSnapshot() {
    this.initialSnapshot = {
      snapshot: this.createSnapshot(),
      id:
        stepManager.getInstance().getCurrentObjective()?.steps[
          stepManager.getInstance().getCurrentStep()
        ].stepID || "0",
    };
  }

  updateInitialSnapshot() {
    this.initialSnapshot = {
      snapshot: this.createSnapshot(),
      id: stepManager.getInstance().getCurrentObjective().steps[
        stepManager.getInstance().getCurrentStep()
      ].stepID,
    };
  }

  saveSnapshot(id: string) {
    const snapshot = this.createSnapshot();

    //Store the snapshot and remove the oldest so that only the remaining number of snapshots are stored
    this.undoSnapshots.push({ snapshot: snapshot, id: id });

    if (this.undoSnapshots.length > this.undoRemaining + 1)
      this.undoSnapshots.shift();
  }

  saveRestartSnapshot(stepId: string) {
    if (
      this.restartSnapshots.findIndex((snapshot) => snapshot.id === stepId) !==
      -1
    ) {
      return;
    }

    let snapshot;

    if (this.undoSnapshots.length === 0)
      snapshot = this.initialSnapshot?.snapshot;
    else snapshot = this.undoSnapshots[this.undoSnapshots.length - 1].snapshot;

    if (snapshot)
      this.restartSnapshots.push({ snapshot: snapshot, id: stepId });
  }

  updateFirstRestartSnapshot(stepId: string) {
    const index = this.restartSnapshots.findIndex(
      (snapshot) => snapshot.id === stepId
    );
    if (index !== -1) {
      const snapshot = this.initialSnapshot?.snapshot;

      if (snapshot)
        this.restartSnapshots[index] = {
          snapshot: snapshot,
          id: stepId,
        };
    }
  }

  unsaveRestartSnapshot(stepId: string) {
    const snapShots = this.restartSnapshots.filter(
      (item) => item.id !== stepId
    );
    this.restartSnapshots.length = 0;
    this.restartSnapshots.push(...snapShots);
  }

  applyRestartSnapshot(stepId: string) {
    if (this.restartSnapshots.findIndex((item) => item.id === stepId) === -1)
      return;

    const snapshot = this.restartSnapshots.filter(
      (item) => item.id === stepId
    )[0].snapshot;

    snapshot.items.map((item) => {
      const IOItem = this.getObject(item.itemID);
      if (item.state) IOItem?.setItemState(item.state);
      IOItem?.setItemPosition(item.position);
      IOItem?.updateUserInput(item.userInput);
      if (IOItem && "changeText" in IOItem) IOItem?.changeText(item.text);
    });

    stepManager.getInstance().setCurrentObjective(snapshot.currentObjective);
    stepManager.getInstance().setCurrentStep(snapshot.currentStep);
    stepManager.getInstance().updateAllObjectives(snapshot.stepsList);
    stepManager.getInstance().setObjectiveIndex(snapshot.currentObjectiveIndex);

    let index = this.restartSnapshots.findIndex((item) => item.id === stepId);
    if (index !== -1) this.restartSnapshots.length = index;

    index = this.undoSnapshots.findIndex((item) => item.id === stepId);
    if (index === -1 && this.initialSnapshot)
      this.undoSnapshots.push(this.initialSnapshot);
    else this.undoSnapshots.length = index;
  }

  applySnapshot() {
    let snapshot;
    if (this.undoSnapshots.length < 2)
      snapshot = this.initialSnapshot?.snapshot;
    else snapshot = this.undoSnapshots[this.undoSnapshots.length - 2].snapshot;

    if (snapshot == null) return;

    snapshot.items.map((item) => {
      const IOItem = this.getObject(item.itemID);
      if (item.state) IOItem?.setItemState(item.state);
      IOItem?.setItemPosition(item.position);
      IOItem?.updateUserInput(item.userInput);
      if (IOItem && "changeText" in IOItem) IOItem?.changeText(item.text);
    });

    stepManager.getInstance().setCurrentObjective(snapshot.currentObjective);
    stepManager.getInstance().setCurrentStep(snapshot.currentStep);
    stepManager.getInstance().updateAllObjectives(snapshot.stepsList);
    stepManager.getInstance().setObjectiveIndex(snapshot.currentObjectiveIndex);

    if (this.undoSnapshots.length > 1) this.undoSnapshots.pop();
  }

  redoButtonPressed() {
    if (this.undoRemaining > 0) {
      this.applySnapshot();
      this.undoRemaining--;
    }
  }

  loadDatafromJson(simData: object, stepsData: object) {
    this.IOObjects = dataLoader.getInstance().loadDatafromJSon(simData);
    this.IOItems = []; //Reset the items for the components
    this.IOObjects.map((item) => {
      this.addInteractableObject(
        item,
        this.scale.userScale,
        this.scale.sceneScale
      );
    });

    stepManager.getInstance().loadObjectives(stepsData);
    this.initialized = true;
    this.preLoadImages();
  }

  preLoadImages() {
    this.IOObjects.map((item) => {
      if (item.states && item.states.length > 0) {
        item.states.map((state) => {
          graphics.getInstance().preloadImage(state.image);
        });
      }
    });
  }

  setCustomItemDropFunction(
    callback: (
      item: ControllableObject | CustomObject,
      itemDroppedOn: ControllableObject[] | InteractableObject[]
    ) => boolean
  ) {
    this.customItemDropFunction = callback;
  }

  setCustomItemClickFunction(
    callback: (item: InteractableObject | ControllableObject) => boolean
  ) {
    this.customItemClickFunction = callback;
  }

  customClickFunction(item: InteractableObject | ControllableObject) {
    this.customItemClickFunction(item);
  }

  setStepButtonFunction(callback: (stepText: string) => boolean) {
    this.customStepsFunction = callback;
  }

  setAltTextFunction(callback: (item: string) => boolean) {
    this.altTextFunction = callback;
  }

  updateAltTextFunction(item: string) {
    setTimeout(() => {
      this.altTextFunction(item);
    }, 1000);
  }

  updateItemAltText(text: string, item: string) {
    const IOItem = this.getObject(item);

    IOItem?.updateAltText(text);
    return true;
  }

  customButtonFunction(stepsText: string) {
    this.customStepsFunction(stepsText);
  }

  setEjectButtonFunction(
    callback: (item: InteractableObject | ControllableObject) => boolean
  ) {
    this.ejectButtonHandler = callback;
  }

  setMoveMenuFunction(callback: (option: string, item: string) => boolean) {
    this.moveMenuFunction = callback;
  }

  ejectButtonFunction(item: string) {
    let itemObject: InteractableObject[] | ControllableObject[];

    itemObject = this.ControllableObjects.filter((x) => x.props.name === item);

    if (itemObject.length === 0)
      itemObject = this.InteractableObjects.filter(
        (x) => x.props.name === item
      );

    this.ejectButtonHandler(itemObject[0]);
    return true;
  }

  setAddToast(callback: (message: string, type: string) => object) {
    this.addToast = callback;
  }

  setUsingNewDesignSystem(value: boolean) {
    this.usingNewDesignSystem = value;
  }

  //Will add more arguments for controls, states etc later
  addInteractableObject(data: IOData, userScale: number, sceneScale: number) {
    this.IOItems.push(
      IOFactory.getInstance().createObject(data, userScale, sceneScale)
    );
  }

  registerControllableObject(object: ControllableObject) {
    this.ControllableObjects.push(object);
  }

  registerInteractableObject(object: InteractableObject) {
    this.InteractableObjects.push(object);
  }

  registerCompositeInteractableObject(object: CompositeInteractableObject) {
    this.CompositeInteractableObjects.push(object);
  }

  registerSceneObject(object: SceneItem) {
    this.SceneItems.push(object);
  }

  registerCustomObject(object: CustomObject) {
    this.CustomObjects.push(object);
  }

  //Update state of the object
  //object: Id of the item
  //state: Id of the new state
  //return: void
  updateState(
    object: string,
    state: string,
    position?: Vector3,
    reset = false
  ) {
    const itemObject = this.getObject(object);
    if (itemObject) {
      itemObject.setItemState(state);
      if (position || reset) itemObject.setItemPosition(position, reset);
    }
  }

  updatePosition(itemID: string, position: Vector3) {
    const itemObject = this.ControllableObjects.filter(
      (x) => x.props.id === itemID
    );

    if (itemObject.length > 0) itemObject[0].setItemPosition(position);
  }

  setText(id: string, text: string, name?: string) {
    //Check if the item underneath is an interactable object
    const item: InteractableObject[] = this.InteractableObjects.filter(
      (x) => x.props.id === id
    );

    //Check if there is any custom code that is attached to this item's dropping
    if (item.length > 0) {
      item[0].changeText(text, name);
    }
  }

  setLabel(id: string, labelText: string) {
    //Check if the item underneath is an interactable object
    const item: InteractableObject[] = this.InteractableObjects.filter(
      (x) => x.props.id === id
    );
    //If the item exists, update its label text
    if (item.length > 0) {
      const currentLabel = item[0].getLabel();

      // If the current label is a string, replace it with a Label object
      if (typeof currentLabel === "string") {
        const newLabel: Label = {
          text: labelText,
          direction: "vertical",
        };
        item[0].updateLabel(newLabel);
      }
      // If the current label is a Label object, update just its text property
      else if (currentLabel) {
        const updatedLabel: Label = {
          ...(currentLabel as Label),
          text: labelText,
        };
        item[0].updateLabel(updatedLabel);
      }
      // If there's no current label, create a new one with default properties
      else {
        const newLabel: Label = {
          text: labelText,
          direction: "vertical",
        };
        item[0].updateLabel(newLabel);
      }
    }
  }

  handleDragStart(itemDragged: string) {
    SimManager.dispatchAction("ITEM_DRAGGED", {
      itemId: itemDragged,
    });
  }

  handleItemDrop(itemDropped: string, itemDroppedOn: string) {
    //Check this item is dropped
    const droppedItem =
      this.ControllableObjects.find((x) => x.props.id === itemDropped) ||
      this.CustomObjects.find((x) => x.props.id === itemDropped);

    //Check if the item underneath is an interactable object
    let onDropped: InteractableObject[] | ControllableObject[] =
      this.InteractableObjects.filter((x) => x.props.id === itemDroppedOn);

    //If not, check if the item is a controllable item
    if (onDropped.length === 0) {
      onDropped = this.ControllableObjects.filter(
        (x) => x.props.id === itemDroppedOn
      );
    }

    //If the item is dropped on another item
    if (droppedItem && onDropped.length > 0) {
      const droppedState = onDropped[0].onItemDropped(droppedItem);
      droppedItem.onDropped(droppedState);
    }

    //Check if there is any custom code that is attached to this item's dropping
    if (droppedItem) {
      this.customItemDropFunction(droppedItem, onDropped);
    }
  }

  getObject(item: string) {
    let itemObject:
      | InteractableObject
      | ControllableObject
      | CustomObject
      | CompositeInteractableObject
      | undefined;

    itemObject = this.ControllableObjects.find((x) => x.props.id === item);

    if (!itemObject)
      itemObject = this.InteractableObjects.find((x) => x.props.id === item);

    if (!itemObject)
      itemObject = this.CompositeInteractableObjects.find(
        (x) => x.props.id === item
      );

    if (!itemObject)
      itemObject = this.CustomObjects.find((x) => x.props.id === item);

    if (!itemObject)
      itemObject = this.ControllableObjects.find((x) => x.props.name === item);

    if (!itemObject)
      itemObject = this.InteractableObjects.find((x) => x.props.name === item);

    if (!itemObject)
      itemObject = this.CompositeInteractableObjects.find(
        (x) => x.props.name === item
      );

    if (!itemObject)
      itemObject = this.CustomObjects.find((x) => x.props.name === item);

    return itemObject;
  }

  getState(item: string) {
    const itemObject = this.getObject(item);
    if (itemObject) return itemObject.getCurrentState();
    else return null;
  }

  getInputValue(item: string) {
    const itemObject = this.getObject(item);
    if (itemObject) return itemObject.getCurrentInput();
    else return null;
  }

  setInputValue(item: string, value: UserInput) {
    const itemObject = this.getObject(item);
    if (itemObject) return itemObject.updateUserInput(value);
    else return null;
  }

  getInteractableObjects() {
    return this.IOItems;
  }

  getSimDataObject(id: string) {
    return this.IOObjects.find((obj) => obj.id === id);
  }

  getDisplayLabel(id: string): string {
    const simDataObj = this.getSimDataObject(id);
    if (simDataObj?.label?.text) {
      return simDataObj.label.text;
    }
    return id;
  }

  handleUpdateItems() {
    stepManager.getInstance().checkStepsCompleted();
  }

  checkCondition(condition: Condition): boolean {
    if (condition.conditionCheck && condition.conditionValue) {
      if (condition.conditionCheck == "Equal" && condition.conditionValue) {
        for (const key in condition.conditionValue) {
          if (
            this.getInputValue(condition.conditionItem) &&
            key in this.getInputValue(condition.conditionItem)
          ) {
            if (
              this.getInputValue(condition.conditionItem)[key] !==
              condition.conditionValue[key]
            ) {
              return false;
            }
          } else {
            return false;
          }
        }
        return true;
      } else if (
        condition.conditionCheck == "Greater" &&
        condition.conditionValue
      ) {
        if (
          condition.conditionValue > this.getInputValue(condition.conditionItem)
        )
          return true;
        else return false;
      } else if (
        condition.conditionCheck == "Less" &&
        condition.conditionValue
      ) {
        if (
          condition.conditionValue < this.getInputValue(condition.conditionItem)
        )
          return true;
        else return false;
      } else if (
        condition.conditionCheck == "NotEqual" &&
        condition.conditionValue
      ) {
        let conditionKey: {
          key: string;
          value: string | number | boolean | undefined | any[];
        } = { key: "", value: "" };
        let itemKey: {
          key: string;
          value: string | number | boolean | undefined | any[];
        } = { key: "", value: "" };

        //Checking property of condition
        const keys = Object.keys(condition.conditionValue);
        keys.forEach((key) => {
          if (condition.conditionValue)
            conditionKey = {
              key: key,
              value: condition.conditionValue[key],
            };
        });

        //Checking proprety of item
        const currentItemValue = this.getInputValue(condition.conditionItem);

        const keys2 = Object.keys(currentItemValue);
        keys2.forEach((key) => {
          if (currentItemValue && key == conditionKey.key)
            itemKey = { key: key, value: currentItemValue[key] };
        });

        if (
          conditionKey.key == itemKey.key &&
          conditionKey.value != itemKey.value
        )
          return true;
        else return false;
      } else return false;
    } else if (
      this.getState(condition.conditionItem) === condition.conditionState
    ) {
      return true;
    } else return false;
  }

  openModal(name: string, parentName?: string, extraData?: unknown) {
    const userInput = this.getInputValue(name);

    const modalData = {
      ...(userInput ? userInput : {}),
      ...(extraData ? extraData : {}),
    };
    if (
      ModalManager.getInstance().openModal(
        name,
        () =>
          SimManager.dispatchAction("MODAL_LOADED", {
            modalName: name,
            parentName: parentName,
          }),
        modalData
      )
    ) {
      const parentItem = this.InteractableObjects.filter(
        (x) => x.props.name === parentName
      );

      if (parentItem.length > 0) {
        this.OpenModalItem = parentItem[0];
      }
    }
  }

  closedModal(modalName?: string, modalData?: unknown) {
    this.handleUpdateItems();
    SimManager.dispatchAction("CLOSED_SIM_MODAL", { modalName, modalData });
    if (this.OpenModalItem != null) {
      //Process modal close
      this.OpenModalItem = null;
    }
  }

  setScales(userScale: number, sceneScale: number) {
    this.scale = { userScale: userScale, sceneScale: sceneScale };
    this.setScaleForItems(userScale, sceneScale);
  }

  setScaleForItems(userScale: number, sceneScale: number) {
    this.ControllableObjects.forEach((item) => {
      item.setScale(userScale, sceneScale);
    });

    this.InteractableObjects.forEach((item) => {
      item.setScale(userScale, sceneScale);
    });

    this.SceneItems.forEach((item) => {
      item.setScale(userScale, sceneScale);
    });

    this.CustomObjects.forEach((item) => {
      item.setScale(userScale, sceneScale);
    });
  }

  getScales() {
    return this.scale;
  }
}

export default SimManager;
